import { renderGrouped } from "prosemirror-menu";
import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { createChevronLeftSvg, createChevronRightSvg } from "../icons";
import { isFormulaErrorSelected, isImageSelected, isSelectionInCodeBlock } from "../utils";

import { type PanelContent, type PanelType, PanelView } from "./panel";

import { t } from "i18n/i18n";

interface ToolbarPluginOptions {
  content: Record<PanelType, PanelContent>;
}

interface ToolbarPluginState {
  activePanelId: PanelType;
  shouldFocus: boolean; // true for exactly one update cycle, then reset to false
  resetScroll: boolean; // true for exactly one update cycle, then reset to false
}

const SCROLL_STEP_PX = 200;
const SCROLL_TRANSITION_MS = 200;

export const toolbarPluginKey = new PluginKey<ToolbarPluginState>("toolbar");

export function getActivePanelId(state: EditorState): PanelType {
  return toolbarPluginKey.getState(state)?.activePanelId ?? "main";
}

function createScrollButton(
  svg: SVGSVGElement,
  label: string,
  side: "left" | "right",
): { wrapper: HTMLDivElement; button: HTMLButtonElement } {
  const wrapper = document.createElement("div");
  const sideClasses =
    side === "left"
      ? "left-0 bg-gradient-to-r pl-1.5 rounded-bl-md"
      : "right-0 justify-end bg-gradient-to-l pr-1.5 rounded-br-md";
  wrapper.className = `absolute inset-y-0 z-10 w-12 flex items-center opacity-0 pointer-events-none from-[var(--toolbar-bg)] from-75% ${sideClasses}`;
  wrapper.style.transition = `opacity ${SCROLL_TRANSITION_MS}ms ease-in-out`;

  const button = document.createElement("button");
  button.type = "button";
  button.title = label;
  button.ariaLabel = label;
  button.tabIndex = -1; // pointer-only; excluded from roving tabindex
  button.className =
    "size-7 flex items-center justify-center rounded text-black-tint-20 hover:bg-blue-tint-90 hover:text-blue";

  button.appendChild(svg);
  wrapper.appendChild(button);
  return { wrapper, button };
}

function setScrollButtonVisible(wrapper: HTMLDivElement, show: boolean): void {
  if (wrapper.classList.contains("opacity-100") !== show) {
    wrapper.classList.toggle("opacity-100", show);
    wrapper.classList.toggle("opacity-0", !show);
    wrapper.classList.toggle("pointer-events-none", !show);
    wrapper.classList.toggle("pointer-events-auto", show);
  }
}

class ToolbarView {
  private readonly wrapper: HTMLElement;

  private readonly toolbar: HTMLElement;

  private readonly scrollContainer: HTMLDivElement;

  private readonly contentWrapper: HTMLDivElement;

  private readonly leftScrollWrapper: HTMLDivElement;

  private readonly rightScrollWrapper: HTMLDivElement;

  private readonly leftScrollButton: HTMLButtonElement;

  private readonly rightScrollButton: HTMLButtonElement;

  private readonly content: Record<PanelType, PanelContent>;

  private activePanel!: PanelView;

  private readonly resizeObserver: ResizeObserver;

  private readonly onScrollLeft = () =>
    this.scrollContainer.scrollBy({ left: -SCROLL_STEP_PX, behavior: "smooth" });

  private readonly onScrollRight = () =>
    this.scrollContainer.scrollBy({ left: SCROLL_STEP_PX, behavior: "smooth" });

  private readonly updateScrollVisibility = (): void => {
    const scrollLeft = Math.round(this.scrollContainer.scrollLeft);
    const scrollWidth = Math.round(this.scrollContainer.scrollWidth);
    const clientWidth = Math.round(this.scrollContainer.clientWidth);
    setScrollButtonVisible(this.leftScrollWrapper, scrollLeft > 0);
    setScrollButtonVisible(this.rightScrollWrapper, scrollLeft + clientWidth < scrollWidth);
  };

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    this.activePanel.handleKeydown(event);
  };

  constructor(
    private readonly editorView: EditorView,
    options: ToolbarPluginOptions,
  ) {
    this.content = options.content;

    // --- Wrapper & toolbar element ---
    this.wrapper = document.createElement("div");
    this.wrapper.className = "rich-text-toolbar-wrapper";

    this.toolbar = this.wrapper.appendChild(document.createElement("div"));
    this.toolbar.className = "rich-text-toolbar";
    this.toolbar.setAttribute("role", "toolbar");
    this.toolbar.setAttribute("aria-keyshortcuts", "Alt+F10");
    this.toolbar.ariaControlsElements = [editorView.dom];

    // Position "after": replace the editor in the DOM, then re-insert it as the first child
    // so the editor appears above the toolbar in the flex column.
    if (editorView.dom.parentNode) {
      editorView.dom.parentNode.replaceChild(this.wrapper, editorView.dom);
    }
    this.wrapper.insertBefore(editorView.dom, this.wrapper.firstChild);

    // --- Scroll container ---
    const left = createScrollButton(
      createChevronLeftSvg(),
      t("worksheet.rich_text_editor.scroll_left"),
      "left",
    );
    this.leftScrollWrapper = left.wrapper;
    this.leftScrollButton = left.button;

    const right = createScrollButton(
      createChevronRightSvg(),
      t("worksheet.rich_text_editor.scroll_right"),
      "right",
    );
    this.rightScrollWrapper = right.wrapper;
    this.rightScrollButton = right.button;

    this.scrollContainer = document.createElement("div");
    this.scrollContainer.className =
      "toolbar-scroll-container flex-1 min-w-0 flex items-center py-px";

    this.contentWrapper = document.createElement("div");
    this.contentWrapper.className = "flex items-center w-max";
    this.scrollContainer.appendChild(this.contentWrapper);

    this.toolbar.appendChild(this.leftScrollWrapper);
    this.toolbar.appendChild(this.scrollContainer);
    this.toolbar.appendChild(this.rightScrollWrapper);

    // --- Mount initial panel ---
    this.mountPanel("main");

    // --- Event listeners ---
    this.toolbar.addEventListener("keydown", this.handleKeydown);
    this.leftScrollButton.addEventListener("click", this.onScrollLeft);
    this.rightScrollButton.addEventListener("click", this.onScrollRight);
    this.scrollContainer.addEventListener("scroll", this.updateScrollVisibility);

    this.resizeObserver = new ResizeObserver(this.updateScrollVisibility);
    this.resizeObserver.observe(this.scrollContainer); // reacts to container width changes (i.e. editor resize)
    this.resizeObserver.observe(this.contentWrapper); // reacts to content width changes (i.e. panel switch)

    this.update(editorView);
  }

  /**
   * Renders `panel`'s items into contentWrapper and wires a fresh PanelView.
   * Also resets scroll to the start of the new panel's content.
   */
  private mountPanel(panel: PanelType): void {
    const { dom, update, focusables } = renderGrouped(this.editorView, this.content[panel]);
    this.contentWrapper.replaceChildren(dom);
    this.activePanel = new PanelView({ update, focusables });
    this.scrollContainer.scrollLeft = 0;
  }

  update(editorView: EditorView, prevState?: EditorState): void {
    if (prevState) {
      const prevPanelId = getActivePanelId(prevState);
      const nextPanelId = getActivePanelId(editorView.state);

      if (prevPanelId !== nextPanelId) this.mountPanel(nextPanelId);
      const pluginState = toolbarPluginKey.getState(editorView.state);
      if (pluginState?.shouldFocus) this.activePanel.setFocus();
      if (pluginState?.resetScroll) this.scrollContainer.scrollLeft = 0;
    }

    this.activePanel.update(editorView.state);
    this.updateScrollVisibility();
  }

  destroy(): void {
    this.toolbar.removeEventListener("keydown", this.handleKeydown);
    this.leftScrollButton.removeEventListener("click", this.onScrollLeft);
    this.rightScrollButton.removeEventListener("click", this.onScrollRight);
    this.scrollContainer.removeEventListener("scroll", this.updateScrollVisibility);
    this.resizeObserver.disconnect();
    if (this.wrapper.parentNode) {
      this.wrapper.parentNode.replaceChild(this.editorView.dom, this.wrapper);
    }
  }
}

export function toolbarPlugin(options: ToolbarPluginOptions): Plugin<ToolbarPluginState> {
  return new Plugin<ToolbarPluginState>({
    key: toolbarPluginKey,
    state: {
      init: () => ({ activePanelId: "main", shouldFocus: false, resetScroll: false }),
      apply(tr, value) {
        const meta = tr.getMeta(toolbarPluginKey) as Partial<ToolbarPluginState> | undefined;
        return {
          activePanelId: meta?.activePanelId ?? value.activePanelId,
          shouldFocus: meta?.shouldFocus ?? false,
          resetScroll: meta?.resetScroll ?? false,
        };
      },
    },
    appendTransaction(_transactions, _oldState, newState) {
      if (toolbarPluginKey.getState(newState)?.activePanelId !== "textColor") return null;

      // Text color panel is incompatible with these selection types — reset to the default panel.
      if (
        isSelectionInCodeBlock(newState) ||
        isFormulaErrorSelected(newState) ||
        isImageSelected(newState)
      ) {
        return newState.tr.setMeta(toolbarPluginKey, { activePanelId: "main" });
      }

      return null;
    },
    view(editorView) {
      return new ToolbarView(editorView, options);
    },
  });
}
